import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { engageKeys } from "../shared/engage-keys";
import type { ConversationDetail, ConversationItem, ConversationPage, InboxView } from "./inbox-model";

/** When a running snooze ends (`null` once the time has passed or there is none: the server drops it the same way). */
export function snoozeEnd(item: Pick<ConversationItem, "snoozed_until"> | undefined, now: number): string | null {
  const until = item?.snoozed_until;
  return until && new Date(until).getTime() > now ? until : null;
}

/** "3:00 PM" for a snooze that ends today in the workspace time zone, else "Nov 2, 8:00 AM". */
export function snoozeWhen(until: string, now: number, fmt: { time: (iso: string) => string; dateTime: (iso: string) => string; timeZone: string }): string {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: fmt.timeZone });
  return day.format(new Date(until)) === day.format(new Date(now)) ? fmt.time(until) : fmt.dateTime(until);
}

const withoutSnooze = (item: ConversationItem): ConversationItem => ({ ...item, snoozed_until: null });

/** A list page after the conversation was unsnoozed: gone from the Snoozed view, awake everywhere else, and the Snoozed count one less. */
function unsnoozedPage(page: ConversationPage, id: string, view: InboxView, wasSnoozed: boolean): ConversationPage {
  const items = view === "snoozed" ? page.items.filter((item) => item.id !== id) : page.items.map((item) => (item.id === id ? withoutSnooze(item) : item));
  const snoozed = page.counts.snoozed;
  return { ...page, items, counts: wasSnoozed && snoozed !== undefined ? { ...page.counts, snoozed: Math.max(0, snoozed - 1) } : page.counts };
}

export function unsnoozedList(data: InfiniteData<ConversationPage, string | null>, id: string, view: InboxView, now: number): InfiniteData<ConversationPage, string | null> {
  const wasSnoozed = data.pages.some((page) => page.items.some((item) => item.id === id && snoozeEnd(item, now) !== null));
  return { ...data, pages: data.pages.map((page) => unsnoozedPage(page, id, view, wasSnoozed)) };
}

export function unsnoozedDetail(detail: ConversationDetail): ConversationDetail {
  return { ...detail, conversation: withoutSnooze(detail.conversation) };
}

export type CacheSnapshot = readonly (readonly [readonly unknown[], unknown])[];

const isList = (key: readonly unknown[]): boolean => key[3] === "list";
const isDetail = (key: readonly unknown[], id: string): boolean => key[3] === "detail" && key[4] === id;
const viewOf = (key: readonly unknown[]): InboxView => {
  const filters = key[4];
  return typeof filters === "object" && filters !== null && "view" in filters && typeof filters.view === "string" ? (filters.view as InboxView) : "all";
};

/**
 * Show the conversation as awake before the server answers: cancels in-flight reads, patches every cached list and the open
 * thread, and returns what it replaced so a failed request can put it back.
 */
export async function optimisticUnsnooze(client: QueryClient, wsId: string, id: string, now = Date.now()): Promise<CacheSnapshot> {
  const root = engageKeys.inbox(wsId);
  await client.cancelQueries({ queryKey: root });
  const snapshot = client.getQueriesData({ queryKey: root });
  for (const [key, data] of snapshot) {
    if (data === undefined) continue;
    if (isList(key)) client.setQueryData(key, unsnoozedList(data as InfiniteData<ConversationPage, string | null>, id, viewOf(key), now));
    else if (isDetail(key, id)) client.setQueryData(key, unsnoozedDetail(data as ConversationDetail));
  }
  return snapshot;
}

export function restoreSnapshot(client: QueryClient, snapshot: CacheSnapshot | undefined): void {
  for (const [key, data] of snapshot ?? []) client.setQueryData(key, data);
}
